/**
 * FAQ Help Center – JavaScript
 * =============================
 * Features:
 *  - Live search with accent normalization & highlight
 *  - Accordion expand/collapse with animation
 *  - Anchor scroll + auto-open on click or URL hash
 *  - No external dependencies
 */

(function () {
  'use strict';

  /* ── Utilities ── */

  /**
   * Normalize a string: lowercase + remove diacritics (accents).
   * @param {string} str
   * @returns {string}
   */
  function normalize(str) {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Escape special regex characters.
   * @param {string} str
   * @returns {string}
   */
  function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Wrap matching text in a <mark> element.
   * Works with accented characters by normalizing both source and query.
   * @param {string} original – the original HTML-safe text
   * @param {string} query – the raw search query
   * @returns {string} – HTML with <mark> wrappers
   */
  function highlightText(original, query) {
    if (!query) return original;

    var normOriginal = normalize(original);
    var normQuery = normalize(query);
    var escaped = escapeRegex(normQuery);
    var regex = new RegExp(escaped, 'gi');
    var result = '';
    var lastIndex = 0;
    var match;

    while ((match = regex.exec(normOriginal)) !== null) {
      var start = match.index;
      var end = start + match[0].length;
      result += original.slice(lastIndex, start);
      result +=
        '<mark class="faq-hc__highlight">' +
        original.slice(start, end) +
        '</mark>';
      lastIndex = end;
    }

    result += original.slice(lastIndex);
    return result;
  }

  /**
   * Smooth-scroll to an element and open its accordion.
   * @param {HTMLElement} accItem
   */
  function scrollToAndOpen(accItem) {
    if (!accItem) return;

    var trigger = accItem.querySelector('[data-faq-trigger]');
    if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
      toggleAccordion(trigger);
    }

    // Small delay so the panel opens before scrolling
    setTimeout(function () {
      accItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  /* ── Accordion ── */

  /**
   * Toggle an accordion panel open/closed with animation.
   * @param {HTMLElement} trigger – the <button> trigger
   */
  function toggleAccordion(trigger) {
    var isOpen = trigger.getAttribute('aria-expanded') === 'true';
    var panelId = trigger.getAttribute('aria-controls');
    var panel = document.getElementById(panelId);
    if (!panel) return;

    if (isOpen) {
      // Close
      panel.style.maxHeight = panel.scrollHeight + 'px';
      // Force reflow
      panel.offsetHeight; // eslint-disable-line no-unused-expressions
      panel.style.maxHeight = '0';
      panel.classList.remove('faq-hc__acc-panel--open');
      trigger.setAttribute('aria-expanded', 'false');

      panel.addEventListener(
        'transitionend',
        function handler() {
          panel.setAttribute('hidden', '');
          panel.removeEventListener('transitionend', handler);
        },
        { once: true }
      );
    } else {
      // Open
      panel.removeAttribute('hidden');
      panel.style.maxHeight = '0';
      // Force reflow
      panel.offsetHeight; // eslint-disable-line no-unused-expressions
      panel.classList.add('faq-hc__acc-panel--open');
      panel.style.maxHeight = panel.scrollHeight + 'px';
      trigger.setAttribute('aria-expanded', 'true');

      panel.addEventListener(
        'transitionend',
        function handler() {
          // Allow content to grow if it changes
          panel.style.maxHeight = 'none';
          panel.removeEventListener('transitionend', handler);
        },
        { once: true }
      );
    }
  }

  /**
   * Force-open an accordion (without toggle).
   * @param {HTMLElement} trigger
   */
  function forceOpenAccordion(trigger) {
    if (trigger.getAttribute('aria-expanded') !== 'true') {
      toggleAccordion(trigger);
    }
  }

  /* ── Init per section ── */

  function initFaqSection(root) {
    var searchInput = root.querySelector('[data-faq-search]');
    var clearBtn = root.querySelector('[data-faq-clear]');
    var clearBtnAlt = root.querySelector('[data-faq-clear-btn]');
    var noResults = root.querySelector('[data-faq-no-results]');
    var grid = root.querySelector('[data-faq-grid]');

    // Index all searchable items
    var cardItems = Array.prototype.slice.call(
      root.querySelectorAll('[data-faq-card-item]')
    );
    var accItems = Array.prototype.slice.call(
      root.querySelectorAll('[data-faq-acc-item]')
    );
    var cards = Array.prototype.slice.call(
      root.querySelectorAll('[data-faq-card]')
    );
    var accGroups = Array.prototype.slice.call(
      root.querySelectorAll('[data-faq-acc-group]')
    );

    // Build index for search (pre-normalized)
    var searchIndex = accItems.map(function (el) {
      return {
        el: el,
        id: el.id,
        category: el.getAttribute('data-category'),
        question: el.getAttribute('data-question') || '',
        answer: el.getAttribute('data-answer') || '',
        tags: el.getAttribute('data-tags') || '',
        questionNorm: normalize(el.getAttribute('data-question') || ''),
        answerNorm: normalize(el.getAttribute('data-answer') || ''),
        tagsNorm: normalize(el.getAttribute('data-tags') || ''),
      };
    });

    // Map card items to their target accordion item
    var cardItemMap = {};
    cardItems.forEach(function (ci) {
      var targetId = ci.getAttribute('data-target-id');
      cardItemMap[targetId] = ci;
    });

    /* ── Search logic ── */
    var debounceTimer;

    function onSearchInput() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(performSearch, 150);
    }

    function performSearch() {
      var query = searchInput.value.trim();
      var normQuery = normalize(query);

      // Show/hide clear button
      if (clearBtn) {
        clearBtn.hidden = query.length === 0;
      }

      // No query → show all
      if (!normQuery) {
        resetAll();
        return;
      }

      var visibleCategories = {};
      var totalVisible = 0;

      searchIndex.forEach(function (item) {
        var match =
          item.questionNorm.indexOf(normQuery) !== -1 ||
          item.answerNorm.indexOf(normQuery) !== -1 ||
          item.tagsNorm.indexOf(normQuery) !== -1;

        // Accordion item
        if (match) {
          item.el.removeAttribute('hidden');
          totalVisible++;
          visibleCategories[item.category] = true;

          // Highlight question text in accordion
          var qText = item.el.querySelector('[data-faq-question-text]');
          if (qText) {
            qText.innerHTML = highlightText(item.question, query);
          }
        } else {
          item.el.setAttribute('hidden', '');

          // Close if it was open
          var trigger = item.el.querySelector('[data-faq-trigger]');
          if (trigger && trigger.getAttribute('aria-expanded') === 'true') {
            toggleAccordion(trigger);
          }

          // Reset highlight
          var qText = item.el.querySelector('[data-faq-question-text]');
          if (qText) {
            qText.textContent = item.question;
          }
        }

        // Corresponding card item
        var cardItem = cardItemMap[item.id];
        if (cardItem) {
          if (match) {
            cardItem.removeAttribute('hidden');
            // Highlight link text
            var link = cardItem.querySelector('[data-faq-link]');
            if (link) {
              link.innerHTML = highlightText(item.question, query);
            }
          } else {
            cardItem.setAttribute('hidden', '');
            var link = cardItem.querySelector('[data-faq-link]');
            if (link) {
              link.textContent = item.question;
            }
          }
        }
      });

      // Show/hide category cards
      cards.forEach(function (card) {
        var cat = card.getAttribute('data-category');
        if (visibleCategories[cat]) {
          card.removeAttribute('hidden');
          // Update count
          var count = card.querySelectorAll(
            '[data-faq-card-item]:not([hidden])'
          ).length;
          var countEl = card.querySelector('.faq-hc__card-count');
          if (countEl) {
            countEl.textContent =
              count + (count === 1 ? ' artículo' : ' artículos') + ' en esta sección';
          }
        } else {
          card.setAttribute('hidden', '');
        }
      });

      // Show/hide accordion groups
      accGroups.forEach(function (group) {
        var cat = group.getAttribute('data-category');
        if (visibleCategories[cat]) {
          group.removeAttribute('hidden');
        } else {
          group.setAttribute('hidden', '');
        }
      });

      // No results message
      if (noResults) {
        noResults.hidden = totalVisible > 0;
      }
    }

    function resetAll() {
      // Show everything, remove highlights
      searchIndex.forEach(function (item) {
        item.el.removeAttribute('hidden');

        var qText = item.el.querySelector('[data-faq-question-text]');
        if (qText) {
          qText.textContent = item.question;
        }

        var cardItem = cardItemMap[item.id];
        if (cardItem) {
          cardItem.removeAttribute('hidden');
          var link = cardItem.querySelector('[data-faq-link]');
          if (link) {
            link.textContent = item.question;
          }
        }
      });

      cards.forEach(function (card) {
        card.removeAttribute('hidden');
        // Restore original count
        var count = card.querySelectorAll('[data-faq-card-item]').length;
        var countEl = card.querySelector('.faq-hc__card-count');
        if (countEl) {
          countEl.textContent =
            count + (count === 1 ? ' artículo' : ' artículos') + ' en esta sección';
        }
      });

      accGroups.forEach(function (group) {
        group.removeAttribute('hidden');
      });

      if (noResults) {
        noResults.hidden = true;
      }
    }

    function clearSearch() {
      searchInput.value = '';
      if (clearBtn) clearBtn.hidden = true;
      resetAll();
      searchInput.focus();
    }

    /* ── Event listeners ── */

    // Search
    if (searchInput) {
      searchInput.addEventListener('input', onSearchInput);
    }

    // Clear buttons
    if (clearBtn) {
      clearBtn.addEventListener('click', clearSearch);
    }
    if (clearBtnAlt) {
      clearBtnAlt.addEventListener('click', clearSearch);
    }

    // Accordion triggers (event delegation)
    root.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-faq-trigger]');
      if (trigger) {
        e.preventDefault();
        toggleAccordion(trigger);
      }
    });

    // Card links → scroll to accordion and open
    root.addEventListener('click', function (e) {
      var link = e.target.closest('[data-faq-link]');
      if (link) {
        e.preventDefault();
        var href = link.getAttribute('href');
        if (!href) return;
        var targetId = href.replace('#', '');
        var target = document.getElementById(targetId);
        if (target) {
          scrollToAndOpen(target);
          // Update URL hash without jumping
          history.pushState(null, '', href);
        }
      }
    });

    // Handle URL hash on load
    function handleHash() {
      var hash = window.location.hash;
      if (hash) {
        var target = document.getElementById(hash.replace('#', ''));
        if (target && target.closest('.faq-hc__acc-item')) {
          setTimeout(function () {
            scrollToAndOpen(target);
          }, 300);
        }
      }
    }

    handleHash();
    window.addEventListener('hashchange', handleHash);

    // Keyboard: Enter/Space on triggers
    root.addEventListener('keydown', function (e) {
      var trigger = e.target.closest('[data-faq-trigger]');
      if (trigger && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        toggleAccordion(trigger);
      }
    });
  }

  /* ── Bootstrap ── */

  function init() {
    var sections = document.querySelectorAll('.faq-hc');
    for (var i = 0; i < sections.length; i++) {
      initFaqSection(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: reinitialize on section load/select
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target.querySelector('.faq-hc');
      if (root) initFaqSection(root);
    });
  }
})();
